import { getBookingFlowAvailabilityUrl } from '../firebase/runtimeEndpoints.ts';
import {
  isBookingFlowAvailabilityRequest,
  isBookingFlowAvailabilityResponse,
  responseMatchesAvailabilityRequest,
  type BookingFlowAvailabilityRequest,
  type BookingFlowAvailabilityResponse,
} from './bookingFlowAvailabilityContract.ts';

export type BookingFlowAvailabilityErrorKind =
  | 'invalid_request'
  | 'aborted'
  | 'transport_error'
  | 'http_error'
  | 'invalid_response';

export class BookingFlowAvailabilityError extends Error {
  readonly kind: BookingFlowAvailabilityErrorKind;
  readonly status?: number;

  constructor(
    kind: BookingFlowAvailabilityErrorKind,
    message: string,
    status?: number,
  ) {
    super(message);
    this.name = 'BookingFlowAvailabilityError';
    this.kind = kind;
    this.status = status;
  }
}

export interface BookingFlowAvailabilityServiceOptions {
  endpointUrl?: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}

const isAbortError = (error: unknown) => Boolean(error)
  && typeof error === 'object'
  && 'name' in error
  && (error as { name?: unknown }).name === 'AbortError';

export const requestBookingFlowAvailability = async (
  request: BookingFlowAvailabilityRequest,
  options: BookingFlowAvailabilityServiceOptions = {},
): Promise<BookingFlowAvailabilityResponse> => {
  if (!isBookingFlowAvailabilityRequest(request)) {
    throw new BookingFlowAvailabilityError('invalid_request', 'Invalid availability request');
  }

  const fetchImpl = options.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(options.endpointUrl ?? getBookingFlowAvailabilityUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: options.signal,
    });
  } catch (error) {
    if (options.signal?.aborted || isAbortError(error)) {
      throw new BookingFlowAvailabilityError('aborted', 'Availability request aborted');
    }
    throw new BookingFlowAvailabilityError('transport_error', 'Availability request failed');
  }

  const body = await response.json().catch(() => null) as unknown;
  if (!response.ok) {
    throw new BookingFlowAvailabilityError('http_error', 'Availability endpoint rejected the request', response.status);
  }
  if (!isBookingFlowAvailabilityResponse(body) || !responseMatchesAvailabilityRequest(body, request)) {
    throw new BookingFlowAvailabilityError('invalid_response', 'Availability endpoint returned an invalid response');
  }
  return body;
};
